import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {AccountPublicBusinessDetailsInput} from 'api/generated';
import {AnchorLink, Hint, PhoneNumberInput, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select} from 'formik-antd';
import {useCountries} from 'hooks/useCountries';
import {formatAddress, formatEmail, formatPhone, formatWebsite} from 'lib/formaters';
import {validatePhone} from 'lib/validation';
import React, {useState} from 'react';
import * as yup from 'yup';
import {SectionProps} from './types';
import {UrlInput} from 'components/Form/UrlInput';
import {omitEmpty} from 'lib/omitEmpty';

const {Option} = Select;

export const PublicBusinessDetails: React.FC<SectionProps> = ({
  account,
  formProps,
  cardProps,
  onSubmit
}) => {
  const [isEditing, setEditing] = useState(false);
  const {getIsoCountry, isoCountries, getCountryName} = useCountries();
  const business = account?.publicBusinessDetails || {};

  const schema = yup.object().shape({
    companyName: yup
      .string()
      .matches(/^[A-Za-zÀ-ú0-9 .,&_@/#+-]*$/, 'no special characters are allowed')
      .nullable(),
    address: yup
      .object()
      .shape({
        city: yup.string().nullable(),
        country: yup.string().nullable(),
        line1: yup.string().nullable(),
        line2: yup.string().nullable(),
        zip: yup.string().nullable(),
        state: yup.string().nullable()
      })

      .nullable(),
    phone: yup
      .string()
      .test('phone', 'invalid phone format', validatePhone)

      .nullable(),
    website: yup.string().url().nullable(),
    supportWebsite: yup.string().url().nullable(),
    supportPhone: yup
      .string()
      .test('phone', 'invalid phone format', validatePhone)

      .nullable(),
    supportEmail: yup.string().email().nullable()
  });

  const handleSubmit = async (publicBusinessDetails: AccountPublicBusinessDetailsInput) => {
    const data = {publicBusinessDetails};
    await onSubmit({
      ...data,
      id: account?.id!
    });
    setEditing(false);
  };

  const initialValues: AccountPublicBusinessDetailsInput = omitEmpty(business);

  return (
    <Card
      title={
        <Hint
          title={'Public business details'}
          message={'This information will be visible to the end customer.'}
        />
      }
      {...cardProps}
      extra={
        isEditing ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={true}>
          {({values, isSubmitting}) => {
            const selectedCountry = getIsoCountry(values.address?.country);
            return (
              <Form {...formProps}>
                <FormItem
                  name="companyName"
                  label={'Business name'}
                  hasFeedback={false}
                  extra={`Your public business name will be used on the payment page, invoices and receipts sent your customers. Please make sure it's correct.`}>
                  <Input name="companyName" placeholder="My Company" />
                </FormItem>
                <FormItem name="address.country" label={'Country'} hasFeedback={false}>
                  <Select
                    name="address.country"
                    placeholder={'Country'}
                    optionFilterProp="children"
                    showSearch>
                    {isoCountries.map((country) => (
                      <Option key={country.code} value={country.code}>
                        {getCountryName(country.code)}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  name="address.state"
                  label={'State / Province / Region'}
                  hasFeedback={false}>
                  {selectedCountry?.divisions ? (
                    <Select
                      name="address.state"
                      optionFilterProp="children"
                      showSearch
                      placeholder={'State / Province / Region'}>
                      {Object.keys(selectedCountry.divisions)
                        .sort((code1, code2) =>
                          selectedCountry.divisions[code1].localeCompare(
                            selectedCountry.divisions[code2]
                          )
                        )
                        .map((code) => (
                          <Option key={code} value={selectedCountry.divisions[code]}>
                            {selectedCountry.divisions[code]}
                          </Option>
                        ))}
                    </Select>
                  ) : (
                    <Input name="address.state" placeholder={'State / Province / Region'} />
                  )}
                </FormItem>
                <FormItem name="address.city" label={'City'} hasFeedback={false}>
                  <Input name="address.city" placeholder={'City'} />
                </FormItem>
                <FormItem name="address.line1" label={'Address'} hasFeedback={false}>
                  <Input name="address.line1" placeholder={'Street, 123'} />
                </FormItem>
                <FormItem name="address.zip" label={'Zip / Postal code'} hasFeedback={false}>
                  <Input name="address.zip" placeholder={'Zip / Postal code'} />
                </FormItem>
                <FormItem name="website" label={'Business website'} hasFeedback={false}>
                  <UrlInput name="website" placeholder="mycompany.com" />
                </FormItem>
                <FormItem name="phone" label={'Business phone'} hasFeedback={false}>
                  <PhoneNumberInput name="phone" countryCode={values.address?.country} />
                </FormItem>
                <FormItem name="supportEmail" label={'Support email'} hasFeedback={false}>
                  <Input name="supportEmail" placeholder="email@example.com" />
                </FormItem>
                <FormItem name="supportPhone" label={'Support phone'} hasFeedback={false}>
                  <PhoneNumberInput name="supportPhone" countryCode={values.address?.country} />
                </FormItem>
                <FormItem name="supportWebsite" label={'Support website'} hasFeedback={false}>
                  <UrlInput name="supportWebsite" placeholder="mycompany.com" />
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  hasFeedback={false}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <Button htmlType="submit" type="primary" loading={isSubmitting}>
                      Save
                    </Button>
                  </Stack>
                </FormItem>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <PropertyList
          omitEmpty
          emptyText="No public business details provided"
          properties={[
            {label: 'Business name', value: business.companyName},
            {label: 'Business address', value: formatAddress(business.address)},
            {label: 'Business website', value: formatWebsite(business.website)},
            {label: 'Business phone', value: formatPhone(business.phone)},
            {
              label: 'Support email',
              value: formatEmail(business.supportEmail)
            },
            {
              label: 'Support phone',
              value: formatPhone(business.supportPhone)
            },
            {
              label: 'Support website',
              value: formatWebsite(business.supportWebsite)
            }
          ]}
        />
      )}
    </Card>
  );
};
